import { useState, useCallback, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ThemeToggle } from './ThemeToggle';
import { ModuleLegend } from './ModuleLegend';
import { ProgressStats } from './ProgressStats';
import { SyncStatus } from './SyncPanel';
import { DailyAgenda } from './DailyAgenda';
import { MonthlyView } from './MonthlyView';
import { WeeklyView } from './WeeklyView';
import { Calendar, CalendarDays, CalendarRange, GraduationCap } from 'lucide-react';

const STORAGE_KEY = 'study-calendar-completed-tasks';

export const StudyCalendar = () => {
  // Initialize to today's date and default to agenda view
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [activeView, setActiveView] = useState('agenda');
  const [completedTasks, setCompletedTasks] = useState(() => {
    // Load from localStorage on initial render
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved tasks:', e);
        }
      }
    }
    return {};
  });

  // Save to localStorage whenever completedTasks changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedTasks));
    }
  }, [completedTasks]);

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleToggleTask = useCallback((dateStr, taskIndex) => {
    setCompletedTasks(prev => {
      const dayTasks = prev[dateStr] || [];
      const isCompleted = dayTasks.includes(taskIndex);
      
      if (isCompleted) {
        return {
          ...prev,
          [dateStr]: dayTasks.filter(i => i !== taskIndex)
        };
      } else {
        return {
          ...prev,
          [dateStr]: [...dayTasks, taskIndex]
        };
      }
    });
  }, []);

  const handleSyncPull = useCallback((tasks) => {
    setCompletedTasks(tasks);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="study-calendar">
      {/* Header */}
      <header className="app-header border-b-2 border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <GraduationCap className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                  Simon's Study Calendar
                </h1>
                <p className="text-sm text-muted-foreground">
                  MST124 & SM123 | March - June 2026
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <SyncStatus
                completedTasks={completedTasks}
                onSyncPull={handleSyncPull}
              />
              <ModuleLegend />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Calendar Views */}
          <div className="xl:col-span-8 space-y-6">
            <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-3 rounded-xl p-1 bg-muted">
                <TabsTrigger 
                  value="agenda"
                  className="view-toggle-btn rounded-lg font-medium"
                  data-testid="agenda-view-tab"
                >
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Agenda
                </TabsTrigger>
                <TabsTrigger 
                  value="monthly" 
                  className="view-toggle-btn rounded-lg font-medium"
                  data-testid="monthly-view-tab"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Monthly
                </TabsTrigger>
                <TabsTrigger 
                  value="weekly"
                  className="view-toggle-btn rounded-lg font-medium"
                  data-testid="weekly-view-tab"
                >
                  <CalendarRange className="w-4 h-4 mr-2" />
                  Weekly
                </TabsTrigger>
              </TabsList>

              <TabsContent value="monthly" className="mt-6">
                <MonthlyView
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  completedTasks={completedTasks}
                />
              </TabsContent>

              <TabsContent value="weekly" className="mt-6">
                <WeeklyView
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  currentWeek={currentWeek}
                  onWeekChange={setCurrentWeek}
                  completedTasks={completedTasks}
                  onToggleTask={handleToggleTask}
                />
              </TabsContent>

              <TabsContent value="agenda" className="mt-6">
                <DailyAgenda
                  selectedDate={selectedDate}
                  completedTasks={completedTasks}
                  onToggleTask={handleToggleTask}
                  onDateSelect={handleDateSelect}
                  showNavigation={true}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-4 space-y-6">
            {/* Progress Stats */}
            <ProgressStats 
              completedTasks={completedTasks}
              selectedDate={selectedDate}
            />

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <p className="text-center text-xs text-muted-foreground">
            MST124 & SM123 | March – June 2026
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StudyCalendar;
